import type { OnboardingDiscipline, OnboardingTemplate } from "@shared/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { DISCIPLINE_LABELS } from "@/lib/discipline-labels";
import { orpc, rpcClient } from "@/lib/rpc";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function todayBrtIso(): string {
  const now = new Date();
  // BRT = UTC-3. Build the YYYY-MM-DD that is "today" in São Paulo time.
  const brt = new Date(now.getTime() - 3 * 60 * 60 * 1000);
  return brt.toISOString().slice(0, 10);
}

export function StartOnboardingDialog({ open, onOpenChange }: Props) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();
  const { user } = useAuth();

  // Dedicated endpoint gated like starting an onboarding — employees.list is
  // scoped to direct reports for holders of just the `onboarding` permission.
  const employeesQuery = useQuery(orpc.onboardings.listHireCandidates.queryOptions());
  const buddyCandidatesQuery = useQuery(orpc.onboardings.listBuddyCandidates.queryOptions());
  const templatesQuery = useQuery(orpc.onboardingTemplates.list.queryOptions());
  const activeOnboardingsQuery = useQuery(orpc.onboardings.listActive.queryOptions());

  const [employeeId, setEmployeeId] = useState("");
  const [startDate, setStartDate] = useState(todayBrtIso());
  const [templateId, setTemplateId] = useState("");
  const [buddyEmail, setBuddyEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  const employees = employeesQuery.data ?? [];
  const buddyCandidates = buddyCandidatesQuery.data ?? [];
  const templates = (templatesQuery.data as OnboardingTemplate[] | undefined) ?? [];

  const eligibleTemplates = useMemo(
    () => templates.filter((t) => t.state === "published"),
    [templates],
  );

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === employeeId),
    [employees, employeeId],
  );

  // Default buddy = current user, but ONLY if they're actually in the buddy pool.
  // Pre-filling with an ineligible user produces a confusing "não habilitado como
  // buddy" error before the person even touches the field.
  useEffect(() => {
    if (buddyEmail || !user?.email) return;
    const isEligible = buddyCandidates.some(
      (c) => c.corporateEmail?.toLowerCase() === user.email?.toLowerCase(),
    );
    if (isEligible) setBuddyEmail(user.email);
  }, [user?.email, buddyEmail, buddyCandidates]);

  // When an employee is selected, prefill start date from their employment start date.
  useEffect(() => {
    if (selectedEmployee?.startDate) {
      setStartDate(selectedEmployee.startDate);
    }
  }, [selectedEmployee?.startDate]);

  // Clear a field's inline error as soon as the user edits it, so a stale
  // server error (e.g. buddy_not_eligible from a previous submit) doesn't linger
  // after the selection has been corrected.
  function clearError(field: string) {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const { [field]: _removed, ...rest } = prev;
      return rest;
    });
  }

  function reset() {
    setEmployeeId("");
    setStartDate(todayBrtIso());
    setTemplateId("");
    setBuddyEmail(user?.email ?? "");
    setErrors({});
  }

  type StartInput = {
    hireEmail: string;
    hireFullName: string;
    discipline: string;
    level: string;
    startDate: string;
    templateId: string;
    buddyEmail: string;
  };

  function handleError(err: any) {
    const code = err?.data?.code as string | undefined;
    const message = err?.message ?? "Não foi possível iniciar o onboarding. Tente novamente.";
    if (code === "active_onboarding_exists") {
      setErrors({ employeeId: message });
    } else if (code === "start_date_in_past") {
      setErrors({ startDate: message });
    } else if (code === "template_not_eligible") {
      setErrors({ templateId: message });
    } else if (
      code === "buddy_not_found" ||
      code === "buddy_not_eligible" ||
      code === "buddy_at_capacity"
    ) {
      setErrors({ buddyEmail: message });
    } else {
      toast({
        title: "Erro ao iniciar onboarding",
        description: message,
        variant: "destructive",
      });
    }
  }

  const createDraft = useMutation({
    mutationFn: (input: StartInput) => rpcClient.onboardings.createDraft(input),
    onSuccess: (onboarding: any) => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listDrafts.queryOptions().queryKey,
      });
      const name = selectedEmployee?.fullName ?? "";
      const disc = onboarding?.discipline as OnboardingDiscipline | undefined;
      toast({
        title: "Rascunho criado",
        description: disc ? `${name} · ${DISCIPLINE_LABELS[disc]}` : name,
      });
      onOpenChange(false);
      reset();
      navigate(`/onboardings/${onboarding.id}/customize`);
    },
    onError: handleError,
  });

  function buildPayload(): StartInput | null {
    const next: Record<string, string> = {};
    if (!employeeId) next.employeeId = "Campo obrigatório";
    if (!startDate) next.startDate = "Campo obrigatório";
    if (!templateId) next.templateId = "Campo obrigatório";
    if (!buddyEmail.trim()) next.buddyEmail = "Campo obrigatório";

    const employee = employees.find((e) => e.id === employeeId);
    if (employeeId && !employee) {
      next.employeeId = "Colaborador não encontrado";
    } else if (employee) {
      if (!employee.corporateEmail) {
        next.employeeId = "Colaborador sem e-mail corporativo cadastrado";
      }
      if (!employee.compDiscipline) {
        next.employeeId = "Colaborador sem disciplina cadastrada";
      }
      if (!employee.jobLevel) {
        next.employeeId = "Colaborador sem nível cadastrado";
      }
    }

    setErrors(next);
    if (Object.keys(next).length > 0 || !employee) return null;

    return {
      hireEmail: (employee.corporateEmail as string).trim(),
      hireFullName: employee.fullName,
      discipline: employee.compDiscipline as string,
      level: employee.jobLevel as string,
      startDate,
      templateId,
      buddyEmail: buddyEmail.trim(),
    };
  }

  function handleCustomize() {
    const p = buildPayload();
    if (p) createDraft.mutate(p);
  }

  const activeHireEmails = useMemo(
    () =>
      new Set(
        (activeOnboardingsQuery.data ?? [])
          .map((o) => o.hireEmail?.toLowerCase())
          .filter((e): e is string => !!e),
      ),
    [activeOnboardingsQuery.data],
  );

  const employeeOptions = useMemo(
    () =>
      employees
        .filter((e) => !activeHireEmails.has(e.corporateEmail.toLowerCase()))
        .map((e) => ({
          value: e.id,
          label: `${e.fullName} · ${e.corporateEmail}`,
        })),
    [employees, activeHireEmails],
  );

  const buddyOptions = useMemo(
    () =>
      buddyCandidates.map((e) => ({
        value: e.corporateEmail,
        label: `${e.fullName} · ${e.corporateEmail}${
          e.isAvailable === false ? " (em onboarding ativo)" : ""
        }`,
        disabled: e.isAvailable === false,
      })),
    [buddyCandidates],
  );

  const templateOptions = useMemo(
    () => eligibleTemplates.map((t) => ({ value: t.id, label: t.name })),
    [eligibleTemplates],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) reset();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Iniciar onboarding</DialogTitle>
          <DialogDescription>
            Crie um onboarding ativo de 60 dias para um novo colaborador.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Colaborador</Label>
            <SearchableSelect
              value={employeeId || undefined}
              onValueChange={(v) => {
                setEmployeeId(v);
                clearError("employeeId");
              }}
              options={employeeOptions}
              placeholder="Selecione um colaborador"
              data-testid="select-employee"
            />
            {errors.employeeId && (
              <p className="mt-1 text-sm text-destructive">{errors.employeeId}</p>
            )}
          </div>

          <div>
            <Label htmlFor="startDate">Data de início</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="startDate"
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !startDate && "text-muted-foreground",
                  )}
                  data-testid="start-date-trigger"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {startDate
                    ? format(parseISO(startDate), "dd/MM/yyyy", { locale: ptBR })
                    : "Selecione a data"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={startDate ? parseISO(startDate) : undefined}
                  onSelect={(d) => {
                    if (!d) return;
                    setStartDate(format(d, "yyyy-MM-dd"));
                    clearError("startDate");
                  }}
                  disabled={(d) => d < parseISO(todayBrtIso())}
                  initialFocus
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>
            {errors.startDate && (
              <p className="mt-1 text-sm text-destructive">{errors.startDate}</p>
            )}
          </div>

          <div>
            <Label>Template</Label>
            <SearchableSelect
              value={templateId || undefined}
              onValueChange={(v) => {
                setTemplateId(v);
                clearError("templateId");
              }}
              options={templateOptions}
              placeholder="Selecione um template publicado"
              disabled={templateOptions.length === 0}
              data-testid="select-template"
            />
            {templateOptions.length === 0 && (
              <p className="mt-1 text-sm text-destructive">Nenhum template publicado.</p>
            )}
            {errors.templateId && (
              <p className="mt-1 text-sm text-destructive">{errors.templateId}</p>
            )}
          </div>

          <div>
            <Label>Buddy</Label>
            <SearchableSelect
              value={buddyEmail || undefined}
              onValueChange={(v) => {
                setBuddyEmail(v);
                clearError("buddyEmail");
              }}
              options={buddyOptions}
              placeholder="Selecione um colaborador"
              data-testid="select-buddy"
            />
            {errors.buddyEmail && (
              <p className="mt-1 text-sm text-destructive">{errors.buddyEmail}</p>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createDraft.isPending}
            data-testid="button-cancel-start"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleCustomize}
            disabled={createDraft.isPending}
            data-testid="button-customize-before-start"
          >
            {createDraft.isPending ? "Criando rascunho..." : "Personalizar onboarding"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
